/**
 * In-memory store for the current player JWT.
 *
 * The JWT itself is never persisted — it lives only in this in-memory store
 * while the SPA tab is open and is cleared on logout, tab close, or
 * explicit clear().
 *
 * The companion HAS_REFRESH_KEY hint *is* persisted to localStorage so the
 * bootstrap path can skip /user/refresh for visitors that have never logged
 * in. The hint records "this browser holds an HttpOnly REFRESH_TOKEN cookie"
 * — never the token itself, which remains exclusively in the BE-set
 * HttpOnly cookie. Set on successful login or refresh, cleared on logout or
 * terminal refresh failure.
 *
 * setJwt() with an undefined or empty string only clears the in-memory JWT
 * and leaves the persisted hint alone: the refresh coordinator (a refresh
 * response without a token) and the token-login rollback reach it while a
 * refresh cookie may still exist, so it is not an authentication state
 * change. Logins go through applyLoginToken() instead, where the absence of
 * a token is meaningful — the server chose a session login — and clears the
 * hint as well.
 */
export class JwtStore {
  static readonly HAS_REFRESH_KEY = "hasRefresh";

  private jwt: string | undefined = undefined;

  setJwt(jwt: string | undefined): void {
    if (jwt === undefined || jwt === "") {
      this.jwt = undefined;
      return;
    }
    this.jwt = jwt;
    JwtStore.markHasRefresh();
  }

  getJwt(): string | undefined {
    return this.jwt;
  }

  clear(): void {
    this.jwt = undefined;
    JwtStore.clearHasRefresh();
  }

  /**
   * Records the outcome of a successful login. Unlike setJwt(undefined), a login without a token
   * also drops the refresh hint: the server chose a session login for this player, so any refresh
   * cookie from an earlier JWT login is no longer the credential to restore from.
   */
  applyLoginToken(token: string | undefined): void {
    if (token === undefined || token === "") {
      this.clear();
      return;
    }
    this.setJwt(token);
  }

  /**
   * True when this browser authenticates with a JWT — a token in memory, or a refresh cookie
   * recorded by the hint — so a 401 can be recovered by refreshing even on a session-auth operator.
   */
  holdsJwtCredential(): boolean {
    return this.jwt !== undefined || JwtStore.hasRefreshHint();
  }

  /**
   * Returns true when this browser has previously received a refresh token
   * (login or refresh response). The bootstrap path uses this to gate the
   * silent /user/refresh call so anonymous visitors do not generate a 401
   * per page load.
   */
  static hasRefreshHint(): boolean {
    try {
      return globalThis.localStorage?.getItem(JwtStore.HAS_REFRESH_KEY) === "1";
    } catch {
      // localStorage may throw in private-mode Safari or sandboxed contexts.
      // A failed read is indistinguishable from "no hint" — we'd rather skip
      // a refresh than crash boot.
      return false;
    }
  }

  private static markHasRefresh(): void {
    try {
      globalThis.localStorage?.setItem(JwtStore.HAS_REFRESH_KEY, "1");
    } catch {
      // Same private-mode/sandbox guard. A failed write means subsequent
      // boots will skip the refresh; the in-flight session still works.
    }
  }

  private static clearHasRefresh(): void {
    try {
      globalThis.localStorage?.removeItem(JwtStore.HAS_REFRESH_KEY);
    } catch {
      // Same guard. A failed clear leaves a stale hint; next boot will try
      // refresh and the BE will terminally 401, clearing the hint then.
    }
  }
}
